import gql from "graphql-tag";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {
    pythonProgramQuery_program as GqlProgram,
    pythonProgramQuery as GqlResponse,
    pythonProgramQueryVariables as GqlVariables,
} from "./__graphql-types__/pythonProgramQuery.ts";

export const programQuery: GqlOperation<GqlResponse, GqlVariables> = gqlOp(gql`
    query pythonProgramQuery($programId: String!) {
        program: programById(id: $programId) {
            byChild
            category
            codeFormat
            created
            author {
                id
                nickname
                profileRoot
                profile {
                    accessLevel
                }
            }
            hiddenEverywhere: deleted
            description
            spinoffCount: displayableSpinoffCount
            docsUrlPath
            flags
            flaggedByKaids
            isFlaggedByCurrentUser
            height
            hiddenFromHotlist: hideFromHotlist
            id
            imagePath
            originIsProject
            isOwner
            authorKaid
            key
            newUrlPath
            originProgram {
                deleted
                translatedTitle
                url
            }
            restrictPosting
            latestRevision {
                id
                code
                configVersion
                created
                editorType
                folds
            }
            slug
            sumVotesIncremented
            title
            parentCurationNode {
                id
                slug
                relativeUrl
                slug
                translatedTitle
            }
            translatedTitle
            url
            userAuthoredContentType
            upVoted
            width
            definitelyNotSpam
        }
    }
`);

export type UserProgram = {
    id: string;
    author: {
        id: string | null | undefined;
        nickname: string | null | undefined;
        profileRoot: string | null | undefined;
        profile: {
            accessLevel: "COACH" | "PUBLIC" | null | undefined;
        };
    };
    byChild: boolean;
    codeFormat: "MULTI_FILE_JSON" | "SINGLE_FILE";
    isOwner: boolean;
    latestRevision: {
        id: string;
        code: string;
        created: string;
    };
    originProgram: {
        url: string;
        title: string;
    } | null;
    parentCurationNode: {
        slug: string;
        relativeUrl: string;
    };
    restrictPosting: boolean;
    title: string;
    url: string;
    userAuthoredContentType: string;
    upVoted: boolean;
    sumVotesIncremented: number;
    key: string;
    isFlaggedByCurrentUser: boolean;
    flaggedByKaids: Array<string> | null | undefined;
    flags: Array<string> | null | undefined;
    definitelyNotSpam: boolean | null | undefined;
    category: "regular" | "documentation" | "contest";
    hiddenEverywhere: boolean;
    hiddenFromHotlist: boolean;
};

/**
 * _invariant marks a field as invariant for TypeScript and asserts that a field is
 * non-null otherwise, throws an error.
 */
const _invariant = <T>(field: T | null | undefined, fieldName: string): T => {
    if (field == null) {
        throw new KAError(
            `Field is unexpectedly null when mapping GraphQL types: ${fieldName}`,
            Errors.Internal,
        );
    }

    return field;
};

function mapCategory(
    category: GqlProgram["category"],
): UserProgram["category"] {
    switch (category) {
        case "OFFICIAL":
            return "contest";
        case "DOCUMENTATION":
            return "documentation";
        case null:
        case undefined:
            return "regular";
        default:
            throw new KAError(
                `unexpected category: ${category}`,
                Errors.Internal,
            );
    }
}

export function useUserProgram(programId: string): Result<UserProgram> {
    const [programResult] = useQuery(programQuery, {variables: {programId}});
    if (programResult.status !== "success") {
        return programResult;
    }

    const {program} = programResult.data;
    if (program == null) {
        throw new KAError("program is unexpectedly null", Errors.Internal);
    }

    return {
        status: "success",
        data: {
            id: program.id,
            author: {
                id: program.author?.id,
                nickname: program.author?.nickname,
                profileRoot: program.author?.profileRoot,
                profile: {
                    accessLevel: program.author?.profile?.accessLevel,
                },
            },
            byChild: program.byChild,
            codeFormat: program.codeFormat,
            isOwner: program.isOwner,
            latestRevision: {
                id: _invariant(program.latestRevision?.id, "latestRevision.id"),
                code: _invariant(
                    program.latestRevision?.code,
                    "latestRevision.code",
                ),
                created: _invariant(
                    program.latestRevision?.created,
                    "latestRevision.created",
                ),
            },
            originProgram:
                program.originProgram == null
                    ? null
                    : {
                          url: program.originProgram.url,
                          title: program.originProgram.translatedTitle,
                      },
            parentCurationNode: {
                slug: _invariant(
                    program.parentCurationNode?.slug,
                    "parentCurationNode.slug",
                ),
                relativeUrl: _invariant(
                    program.parentCurationNode?.relativeUrl,
                    "parentCurationNode.relativeUrl",
                ),
            },
            restrictPosting: program.restrictPosting,
            title: _invariant(program.title, "title"),
            url: _invariant(program.url, "url"),
            userAuthoredContentType: program.userAuthoredContentType,
            upVoted: program.upVoted,
            sumVotesIncremented: _invariant(
                program.sumVotesIncremented,
                "sumVotesIncremented",
            ),
            key: program.key,
            isFlaggedByCurrentUser: program.isFlaggedByCurrentUser,
            flaggedByKaids: program.flaggedByKaids,
            flags: program.flags,
            definitelyNotSpam: program.definitelyNotSpam,
            category: mapCategory(program.category),
            hiddenEverywhere: program.hiddenEverywhere ?? false,
            hiddenFromHotlist: program.hiddenFromHotlist,
        },
    };
}
